import { Fragment, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaqEntry, fetchFaq } from '../../api/site';
import { SectionLabel } from '../../components/fs';

/**
 * Capabilities FAQ, rendered from the database — the platform console's Site
 * content tab edits it, so a fixed answer never needs a deploy.
 *
 * Answers are plain text with exactly two constructs: a blank line starts a
 * new paragraph, and [label](url) becomes a link (a router Link for /paths,
 * a plain anchor otherwise). Nothing else is interpreted, so an answer can
 * never carry markup into the page.
 *
 * R13 (screen 03): an accordion with one question open at a time — the first
 * one to begin with — under the categories Site content gives them, and "Still
 * stuck? Ask the community" at the foot. Every answer is in the page, and a
 * closed one is `hidden="until-found"`: the browser's find-in-page still
 * reaches it and opens the question it lands on (`beforematch`), as the
 * `<details>` they used to be did. A browser without that value treats it as
 * plain `hidden`.
 */

const LINK_RE = /\[([^\]]+)\]\(([^)\s]+)\)/g;

function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(LINK_RE)) {
    if (m.index! > last) out.push(text.slice(last, m.index));
    const [, label, url] = m;
    out.push(
      url.startsWith('/')
        ? <Link key={m.index} to={url}>{label}</Link>
        : <a key={m.index} href={url} rel="noopener noreferrer">{label}</a>
    );
    last = m.index! + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function renderAnswer(answer: string): ReactNode {
  return answer.split(/\n\s*\n/).map((para, i) => (
    <p key={i}>{renderInline(para.trim())}</p>
  ));
}

/** Categories in the order their first entry appears, entries kept in order. */
function grouped(faq: FaqEntry[]): { title: string; entries: FaqEntry[] }[] {
  const groups: { title: string; entries: FaqEntry[] }[] = [];
  for (const entry of faq) {
    const last = groups[groups.length - 1];
    if (last && last.title === entry.category) last.entries.push(entry);
    else groups.push({ title: entry.category, entries: [entry] });
  }
  return groups;
}

export default function FaqPage() {
  const [faq, setFaq] = useState<FaqEntry[] | null>(null);
  const [error, setError] = useState('');
  /** The one open question; the first when the page loads, none after closing it. */
  const [open, setOpen] = useState<number | null>(null);
  const answers = useRef(new Map<number, HTMLDivElement>());

  // React knows `hidden` only as a boolean, so "until-found" is set here —
  // before paint, so no answer flashes open.
  useLayoutEffect(() => {
    const undo: (() => void)[] = [];
    for (const [id, el] of answers.current) {
      if (id === open) el.removeAttribute('hidden');
      else el.setAttribute('hidden', 'until-found');
      const found = () => setOpen(id);
      el.addEventListener('beforematch', found);
      undo.push(() => el.removeEventListener('beforematch', found));
    }
    return () => undo.forEach((u) => u());
  }, [open, faq]);

  useEffect(() => {
    fetchFaq()
      .then((r) => { setFaq(r.faq); setOpen(r.faq[0]?.id ?? null); })
      .catch((e) => setError(e.message || 'Could not load the FAQ'));
  }, []);

  return (
    <section className="site-page faq-page" data-frame="column">
      <h1 className="site-h1">Questions, answered</h1>
      {error && <div className="site-err" style={{ marginTop: 16 }}>{error}</div>}
      {!faq && !error && <div className="site-empty">Loading…</div>}
      {faq && grouped(faq).map((g) => (
        <Fragment key={g.title}>
          {/* A single category needs no heading over it. */}
          {grouped(faq).length > 1 && <SectionLabel as="h2" className="faq-cat">{g.title}</SectionLabel>}
          <div className="faq-list">
            {g.entries.map((f) => {
              const isOpen = open === f.id;
              return (
                <div key={f.id} className={`faq-item${isOpen ? ' open' : ''}`}>
                  <h3 className="faq-q">
                    <button
                      type="button" id={`faq-q-${f.id}`} aria-expanded={isOpen} aria-controls={`faq-a-${f.id}`}
                      onClick={() => setOpen(isOpen ? null : f.id)}
                    >
                      <span>{f.question}</span>
                      <span className="sign" aria-hidden="true">{isOpen ? '−' : '+'}</span>
                    </button>
                  </h3>
                  <div
                    className="faq-a" id={`faq-a-${f.id}`} role="region" aria-labelledby={`faq-q-${f.id}`}
                    ref={(el) => { if (el) answers.current.set(f.id, el); else answers.current.delete(f.id); }}
                  >
                    {renderAnswer(f.answer)}
                  </div>
                </div>
              );
            })}
          </div>
        </Fragment>
      ))}
      {/* There whether or not the FAQ loads — the forum is the fallback. */}
      {(faq || error) && (
        <p className="faq-more">
          Still stuck? <Link to="/community">Ask the community</Link>
        </p>
      )}
    </section>
  );
}
